import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AdminSidebar from "@/components/AdminSidebar";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Menu, Edit, Sparkles, Image, Users, Settings, Navigation, ArrowRight, Loader2 } from "lucide-react";
import type { User } from "@supabase/supabase-js";

const AdminDashboard = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { navigate('/login'); return; }
        const { data: profile } = await supabase
          .from('profiles').select('is_admin').eq('id', user.id).single();
        if (!profile?.is_admin) { navigate('/login'); return; }
        setUser(user);
      } catch { navigate('/login'); } finally { setLoading(false); }
    };
    checkUser();
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#f6f6f7]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const quickLinks = [
    { title: "Pagina's bewerken", description: "Bewerk de inhoud van alle website pagina's", icon: Edit, href: "/admin/cms", color: "bg-indigo-50 text-indigo-600" },
    { title: "Expertises", description: "Beheer diensten en specialisaties", icon: Sparkles, href: "/admin/cms/expertises", color: "bg-amber-50 text-amber-600" },
    { title: "Media", description: "Upload en beheer afbeeldingen en video's", icon: Image, href: "/admin/cms/media", color: "bg-purple-50 text-purple-600" },
    { title: "Navigatie", description: "Pas het menu en de footer aan", icon: Navigation, href: "/admin/cms/navigation", color: "bg-blue-50 text-blue-600" },
    { title: "Team", description: "Beheer teamleden op de contactpagina", icon: Users, href: "/admin/team", color: "bg-green-50 text-green-600" },
    { title: "Instellingen", description: "Website en e-mail configuratie", icon: Settings, href: "/admin/settings", color: "bg-gray-100 text-gray-600" },
  ];

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <main className="flex-1 overflow-auto bg-[#f6f6f7]">
          <div className="border-b border-gray-200 bg-white px-8 py-6">
            <div className="max-w-5xl mx-auto flex items-center gap-3">
              <SidebarTrigger className="md:hidden">
                <Menu className="h-5 w-5" />
              </SidebarTrigger>
              <div>
                <h1 className="text-xl font-semibold text-[#202223]">Dashboard</h1>
                <p className="text-sm text-[#6d7175] mt-0.5">Welkom terug, {user?.email?.split('@')[0]}</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {quickLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="bg-white rounded-xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-gray-200/60 hover:shadow-[0_3px_12px_rgba(0,0,0,0.08)] hover:border-gray-300/80 transition-all group"
                  >
                    <div className="flex items-start justify-between">
                      <div className={`p-2.5 rounded-lg ${link.color}`}>
                        <link.icon className="h-5 w-5" />
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-gray-400 group-hover:translate-x-0.5 transition-all mt-1" />
                    </div>
                    <h3 className="text-sm font-semibold text-[#202223] mt-4">{link.title}</h3>
                    <p className="text-[13px] text-[#6d7175] mt-1 leading-snug">{link.description}</p>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default AdminDashboard;
